import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AiTopicSettingsService } from '../ai-topic-settings.service';
import { MessageService } from 'primeng/api';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
    selector: 'app-topic-modal',
    templateUrl: './topic-modal.component.html',
    styleUrls: ['./topic-modal.component.css'],
    standalone: false
})
export class TopicModalComponent implements OnInit {
  topicForm: FormGroup;
  loading = false;
  topics: any[] = [];
  editingTopic: any = null;
  showAddForm = false;

  constructor(
    private dialogRef: MatDialogRef<TopicModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private aiTopicService: AiTopicSettingsService,
    private messageService: MessageService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadTopics();
  }

  private initializeForm(): void {
    this.topicForm = this.fb.group({
      topic_name: ['', [Validators.required, Validators.minLength(2)]],
      status: [true]
    });
  }

  loadTopics(): void {
    this.loading = true;
    this.aiTopicService.getAllTopics(this.data.userId, this.data.siteId).subscribe({
      next: (response) => {
        this.topics = response.data || response;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading topics:', error);
        this.loading = false;
        this.showError('Error loading topics. Please try again.');
      }
    });
  }

  showAddTopicForm(): void {
    this.showAddForm = true;
    this.editingTopic = null;
    this.topicForm.reset();
    this.topicForm.patchValue({ status: true });
  }

  editTopic(topic: any): void {
    this.editingTopic = topic;
    this.showAddForm = true;
    this.topicForm.patchValue({
      topic_name: topic.topic_name,
      status: topic.status
    });
  }

  deleteTopic(topic: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you want to delete the topic "${topic.topic_name}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loading = true;
        this.aiTopicService.deleteTopic(topic.topic_id).subscribe({
          next: (response) => {
            this.loading = false;
            this.loadTopics(); // Reload the list
            this.showSuccess('Topic deleted successfully');
          },
          error: (error) => {
            console.error('Error deleting topic:', error);
            this.loading = false;
            this.showError('Error deleting topic. Please try again.');
          }
        });
      }
    });
  }

  onSubmit(): void {
    if (this.topicForm.valid) {
      this.loading = true;
      const formData = this.topicForm.value;

      if (this.editingTopic) {
        // Update existing topic
        const topicData = {
          topic_name: formData.topic_name,
          is_system: this.data.userId === 1,
          status: formData.status
        };

        this.aiTopicService.updateTopic(this.editingTopic.topic_id, topicData).subscribe({
          next: (response) => {
            this.loading = false;
            this.showAddForm = false;
            this.editingTopic = null;
            this.onCancel(true);
            this.showSuccess('Topic updated successfully');
          },
          error: (error) => {
            console.error('Error updating topic:', error);
            this.loading = false;
            this.showError('Error updating topic. Please try again.');
          }
        });
      } else {
        // Add new topic
        const topicData = {
          site_id: this.data.userId === 1 ? null : this.data.siteId,
          topic_name: formData.topic_name,
          is_system: this.data.userId === 1
        };

        this.aiTopicService.addTopic(topicData).subscribe({
          next: (response) => {
            this.loading = false;
            this.showAddForm = false;
            this.onCancel(true);
            this.showSuccess('Topic added successfully');
          },
          error: (error) => {
            console.error('Error adding topic:', error);
            this.loading = false;
            this.showError('Error adding topic. Please try again.');
          }
        });
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  cancelEdit(): void {
    this.showAddForm = false;
    this.editingTopic = null;
    this.topicForm.reset();
  }

  private markFormGroupTouched(): void {
    Object.keys(this.topicForm.controls).forEach(key => {
      const control = this.topicForm.get(key);
      control?.markAsTouched();
    });
  }

  onCancel(result: boolean = false): void {
    this.dialogRef.close(result);
  }

  getFieldError(fieldName: string): string {
    const field = this.topicForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `${fieldName.replace('_', ' ')} is required`;
      }
      if (field.errors['minlength']) {
        return `${fieldName.replace('_', ' ')} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
    }
    return '';
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }
}
